import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/components/feedback/Toaster';
import * as dataQualityApi from '../data-quality.api';
import type { Alert, AlertsQuery, PaginatedEnvelope } from '../data-quality.api';

export function useCustomerTrends(gln: string, months = 6) {
  return useQuery({
    queryKey: ['dataQuality', 'trends', gln, months],
    queryFn: () => dataQualityApi.getTrends(gln, months),
    enabled: !!gln,
  });
}

export function useBatchHistory(gln: string) {
  return useQuery({
    queryKey: ['dataQuality', 'batchHistory', gln],
    queryFn: () => dataQualityApi.getBatchHistory(gln),
    enabled: !!gln,
  });
}

/** FR26: on-demand recalculation. Never cached; invalidates the completeness + trend reads for this client on success. */
export function useEvaluateProduct(gln: string) {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (cinXmlId: string) => dataQualityApi.evaluateProduct(gln, cinXmlId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['completeness', 'client', gln] });
      queryClient.invalidateQueries({ queryKey: ['completeness', 'product', gln] });
      queryClient.invalidateQueries({ queryKey: ['dataQuality', 'trends', gln] });
      addToast({ key: 'dataQuality.recalculate.success' }, 'success');
    },
    onError: () => {
      addToast({ key: 'dataQuality.recalculate.error' }, 'error');
    },
  });
}

const ALERTS_QUERY_PREFIX = ['dataQuality', 'alerts'] as const;

/** FR24: open/resolved alerts. */
export function useAlerts(query: AlertsQuery) {
  return useQuery({
    queryKey: [...ALERTS_QUERY_PREFIX, query],
    queryFn: () => dataQualityApi.getAlerts(query),
  });
}

/**
 * FR24: resolve one alert with optimistic UI. Rolls back on failure (inline error is the
 * caller's responsibility — failures that require action are never toasted, per convention);
 * success is a non-blocking toast.
 */
export function useResolveAlert() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (alertId: string) => dataQualityApi.resolveAlert(alertId),
    onMutate: async (alertId: string) => {
      await queryClient.cancelQueries({ queryKey: ALERTS_QUERY_PREFIX });
      const previous = queryClient.getQueriesData<PaginatedEnvelope<Alert>>({ queryKey: ALERTS_QUERY_PREFIX });
      queryClient.setQueriesData<PaginatedEnvelope<Alert>>({ queryKey: ALERTS_QUERY_PREFIX }, (old) => {
        if (!old) return old;
        return { ...old, data: old.data.filter((alert) => alert.alertId !== alertId) };
      });
      return { previous };
    },
    onError: (_err, _alertId, context) => {
      context?.previous?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
    onSuccess: () => {
      addToast({ key: 'dataQuality.alerts.resolveSuccess' }, 'success');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ALERTS_QUERY_PREFIX });
    },
  });
}

/**
 * FR24: bulk-resolve. No optimistic removal (a partial failure must stay visible, never a
 * silent half-success) — DQS reports only an aggregate `resolvedCount` (fu-3/AC2, never a
 * per-ID breakdown), so the caller compares `data.data.resolvedCount` against
 * `data.data.requestedCount` inline once the mutation settles, then the list is refetched.
 */
export function useBulkResolveAlerts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (alertIds: string[]) => dataQualityApi.bulkResolveAlerts(alertIds),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ALERTS_QUERY_PREFIX });
    },
  });
}

/** FR27: paginated missing-GPC report for a client. */
export function useMissingGpcReport(gln: string, page = 1, pageSize = 25) {
  return useQuery({
    queryKey: ['dataQuality', 'missingGpc', gln, page, pageSize],
    queryFn: () => dataQualityApi.getMissingGpcReport(gln, page, pageSize),
    enabled: !!gln,
  });
}
